import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import { listTechnicianApiService } from '@epics/technicianManagement/api/listTechnicianApiService';
import type { TechnicianListItem } from '@epics/technicianManagement/types/technicianListType';
import type { FilterOption } from '@shared/components/FilterPopup';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;

const isValidStatus = (status: string): boolean => {
  const validStatuses = ['active', 'inactive'];
  return validStatuses.includes(status);
};

const validateStatusString = (statusStr: string): boolean => {
  if (!statusStr) {
    return true;
  }
  const statuses = statusStr.split(',');
  return statuses.every((s) => isValidStatus(s.trim()));
};

const sanitizeStatusString = (
  statusStr: string | null | undefined
): string | undefined => {
  if (!statusStr) {
    return undefined;
  }
  const trimmed = String(statusStr).trim();
  if (!trimmed) {
    return undefined;
  }
  const statuses = trimmed
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s);
  const validStatuses = statuses.filter((s) => isValidStatus(s));
  return validStatuses.length > 0 ? validStatuses.join(',') : undefined;
};

// Parse status URL param into list so we match whole values (e.g. "inactive" must not match "active")
const statusListFromParam = (
  statusStr: string | null | undefined
): string[] => {
  if (!statusStr) {
    return [];
  }
  return statusStr
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s);
};

const defaultStatusOptions: FilterOption[] = [
  { label: 'Aktywny', value: 'active', checked: false },
  { label: 'Nieaktywny', value: 'inactive', checked: false },
];

const defaultAvailabilityOptions: FilterOption[] = [
  { label: 'Dostępny', value: 'available', checked: false },
  { label: 'Zajęty', value: 'busy', checked: false },
];

export const useTechnicianList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  const urlPage = searchParams.get('page');
  const urlSearch = searchParams.get('search');
  const urlLimit = searchParams.get('limit');
  const urlStatus = sanitizeStatusString(searchParams.get('status'));

  const isValidUrlStatus = validateStatusString(urlStatus || '');

  const [technicians, setTechnicians] = useState<TechnicianListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);
  const [page, setPage] = useState(() => {
    const urlPageValue = urlPage ? parseInt(urlPage, 10) : 1;
    return urlPageValue < 1 ? 1 : urlPageValue;
  });
  const [limit, setLimit] = useState(() => {
    const urlLimitValue = urlLimit ? parseInt(urlLimit, 10) : 10;
    return (ALLOWED_LIMITS as readonly number[]).includes(urlLimitValue)
      ? urlLimitValue
      : 10;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);
  const [search, setSearch] = useState(() => {
    const searchValue = (urlSearch || '').trim();
    return searchValue.length > 200
      ? searchValue.substring(0, 200)
      : searchValue;
  });
  const [limitError, setLimitError] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);
  const prevSearchRef = useRef<string>(search);
  const prevDebouncedSearchRef = useRef<string>(debouncedSearch);

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(() => {
    const statusList = statusListFromParam(urlStatus ?? null);
    if (urlStatus && !isValidUrlStatus) {
      return [
        { label: 'Aktywny', value: 'active', checked: false },
        { label: 'Nieaktywny', value: 'inactive', checked: false },
      ];
    }
    if (urlStatus) {
      return [
        {
          label: 'Aktywny',
          value: 'active',
          checked: statusList.includes('active'),
        },
        {
          label: 'Nieaktywny',
          value: 'inactive',
          checked: statusList.includes('inactive'),
        },
      ];
    }
    return [...defaultStatusOptions];
  });

  const [availabilityOptions, setAvailabilityOptions] = useState<
    FilterOption[]
  >(() => [...defaultAvailabilityOptions]);

  const [appliedFilters, setAppliedFilters] = useState<{
    statusOptions: FilterOption[];
    availabilityOptions: FilterOption[];
  }>(() => {
    const statusList = statusListFromParam(urlStatus ?? null);
    if (urlStatus && !isValidUrlStatus) {
      return {
        statusOptions: [...defaultStatusOptions],
        availabilityOptions: [...defaultAvailabilityOptions],
      };
    }
    if (urlStatus) {
      return {
        statusOptions: [
          {
            label: 'Aktywny',
            value: 'active',
            checked: statusList.includes('active'),
          },
          {
            label: 'Nieaktywny',
            value: 'inactive',
            checked: statusList.includes('inactive'),
          },
        ],
        availabilityOptions: [...defaultAvailabilityOptions],
      };
    }
    return {
      statusOptions: [...defaultStatusOptions],
      availabilityOptions: [...defaultAvailabilityOptions],
    };
  });

  // Sync state from URL when URL changes
  useEffect(() => {
    isSyncingFromUrl.current = true;
    const currentUrlPage = searchParams.get('page');
    const currentUrlSearch = searchParams.get('search');
    const currentUrlLimit = searchParams.get('limit');
    const currentUrlStatus = sanitizeStatusString(searchParams.get('status'));

    if (currentUrlPage) {
      const urlPageValue = parseInt(currentUrlPage, 10);
      if (urlPageValue >= 1 && urlPageValue !== page) {
        setPage(urlPageValue);
      }
    } else if (page !== 1) {
      setPage(1);
    }

    if (currentUrlLimit) {
      const urlLimitValue = parseInt(currentUrlLimit, 10);
      if (
        (ALLOWED_LIMITS as readonly number[]).includes(urlLimitValue) &&
        urlLimitValue !== limit
      ) {
        setLimit(urlLimitValue);
      }
    }

    const urlSearchValue = (currentUrlSearch || '').trim();
    if (urlSearchValue !== search.trim()) {
      const validatedSearch =
        urlSearchValue.length > 200
          ? urlSearchValue.substring(0, 200)
          : urlSearchValue;
      setSearch(validatedSearch);
    }

    if (currentUrlStatus && validateStatusString(currentUrlStatus)) {
      const currentStatusList = statusListFromParam(currentUrlStatus);
      const urlStatusOptions: FilterOption[] = [
        {
          label: 'Aktywny',
          value: 'active',
          checked: currentStatusList.includes('active'),
        },
        {
          label: 'Nieaktywny',
          value: 'inactive',
          checked: currentStatusList.includes('inactive'),
        },
      ];
      if (
        urlStatusOptions[0]?.checked !== statusOptions[0]?.checked ||
        urlStatusOptions[1]?.checked !== statusOptions[1]?.checked
      ) {
        setStatusOptions(urlStatusOptions);
      }
    } else if (!currentUrlStatus && statusOptions.some((opt) => opt.checked)) {
      setStatusOptions([...defaultStatusOptions]);
    }

    if (currentUrlStatus && validateStatusString(currentUrlStatus)) {
      const currentStatusList = statusListFromParam(currentUrlStatus);
      const urlAppliedFilters = {
        statusOptions: [
          {
            label: 'Aktywny',
            value: 'active',
            checked: currentStatusList.includes('active'),
          },
          {
            label: 'Nieaktywny',
            value: 'inactive',
            checked: currentStatusList.includes('inactive'),
          },
        ],
        availabilityOptions: appliedFilters.availabilityOptions,
      };
      if (
        urlAppliedFilters.statusOptions[0]?.checked !==
          appliedFilters.statusOptions[0]?.checked ||
        urlAppliedFilters.statusOptions[1]?.checked !==
          appliedFilters.statusOptions[1]?.checked
      ) {
        setAppliedFilters(urlAppliedFilters);
      }
    } else if (
      !currentUrlStatus &&
      appliedFilters.statusOptions.some((opt) => opt.checked)
    ) {
      setAppliedFilters({
        statusOptions: [...defaultStatusOptions],
        availabilityOptions: appliedFilters.availabilityOptions,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, statusOptions, appliedFilters]);

  // Sync URL params with state
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevSearchRef.current = search;
      return;
    }

    const params = new URLSearchParams();
    const prevSearchTrimmed = prevSearchRef.current.trim();
    const currentSearchTrimmed = search.trim();
    const searchChanged = prevSearchTrimmed !== currentSearchTrimmed;

    if (searchChanged) {
      prevSearchRef.current = search;
      params.set('page', '1');
      if (page !== 1) {
        setPage(1);
      }
    } else {
      params.set('page', page.toString());
    }

    params.set('limit', limit.toString());
    if (search) {
      params.set('search', search);
    }

    const selectedStatuses = appliedFilters.statusOptions
      .filter((opt) => opt.checked)
      .map((opt) => opt.value)
      .join(',');
    if (selectedStatuses) {
      params.set('status', selectedStatuses);
    }

    const cols = searchParams.get('cols');
    if (cols) {
      params.set('cols', cols);
    }

    const currentUrlParams = new URLSearchParams(searchParams.toString());
    if (params.toString() !== currentUrlParams.toString()) {
      setSearchParams(params, { replace: false });
    }
  }, [page, limit, search, appliedFilters, setSearchParams, searchParams]);

  const fetchTechnicians = useCallback(async () => {
    try {
      setLoading(true);
      const selectedStatuses = appliedFilters.statusOptions
        .filter((opt) => opt.checked)
        .map((opt) => opt.value)
        .join(',');
      const trimmedSearch = debouncedSearch.trim();

      if (selectedStatuses && !validateStatusString(selectedStatuses)) {
        setAppliedFilters({
          statusOptions: [...defaultStatusOptions],
          availabilityOptions: appliedFilters.availabilityOptions,
        });
        setTechnicians([]);
        setTotalCount(0);
        return;
      }

      const response = await listTechnicianApiService.listTechnicians({
        page,
        limit,
        ...(trimmedSearch && { search: trimmedSearch }),
        ...(selectedStatuses && { status: selectedStatuses }),
      });

      // Redirect to last valid page if current page is out of range
      if (
        response.totalCount > 0 &&
        response.totalPages > 0 &&
        page > response.totalPages
      ) {
        setPage(response.totalPages);
        return;
      }

      setTechnicians(response.data);
      setTotalCount(response.totalCount);

      const hasNoFilters =
        !trimmedSearch &&
        !selectedStatuses &&
        !appliedFilters.availabilityOptions.some((opt) => opt.checked);
      setIsNoFilterFetch(hasNoFilters);
    } catch (error) {
      if (
        error instanceof Error &&
        (error as Error & { status?: number }).status === 403
      ) {
        setAccessError(true);
      } else {
        setTechnicians([]);
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, appliedFilters]);

  useEffect(() => {
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const prevDebouncedTrimmed = prevDebouncedSearchRef.current.trim();
    const currentDebouncedTrimmed = debouncedSearch.trim();
    if (prevDebouncedTrimmed !== currentDebouncedTrimmed && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    fetchTechnicians();
  }, [fetchTechnicians]);

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!(ALLOWED_LIMITS as readonly number[]).includes(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback(
    (newSearch: string) => {
      if (newSearch.length > 200) {
        return;
      }
      const currentSearchTrimmed = search.trim();
      const newSearchTrimmed = newSearch.trim();
      if (
        !isSyncingFromUrl.current &&
        currentSearchTrimmed !== newSearchTrimmed
      ) {
        setPage(1);
      }
      // Immediately show skeleton when clearing an active search so the empty
      // state never flickers during the 300ms debounce window.
      if (!newSearchTrimmed && currentSearchTrimmed) {
        setLoading(true);
      }
      setSearch(newSearch);
    },
    [search]
  );

  const handleApplyFilters = useCallback(() => {
    const selectedStatuses = statusOptions
      .filter((opt) => opt.checked)
      .map((opt) => opt.value)
      .join(',');
    if (selectedStatuses && !validateStatusString(selectedStatuses)) {
      return;
    }
    setAppliedFilters({ statusOptions, availabilityOptions });
    setPage(1);
  }, [statusOptions, availabilityOptions]);

  const handleClearFilters = useCallback(() => {
    const hasAppliedFilters =
      appliedFilters.statusOptions.some((opt) => opt.checked) ||
      appliedFilters.availabilityOptions.some((opt) => opt.checked);
    setStatusOptions([...defaultStatusOptions]);
    setAvailabilityOptions([...defaultAvailabilityOptions]);
    if (hasAppliedFilters) {
      setAppliedFilters({
        statusOptions: [...defaultStatusOptions],
        availabilityOptions: [...defaultAvailabilityOptions],
      });
      setPage(1);
    }
  }, [appliedFilters]);

  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    !appliedFilters.statusOptions.some((opt) => opt.checked) &&
    !appliedFilters.availabilityOptions.some((opt) => opt.checked);

  const prevDebouncedTrimmed = prevDebouncedSearchRef.current.trim();
  const currentDebouncedTrimmed = debouncedSearch.trim();
  const justClearedSearch =
    prevDebouncedTrimmed !== '' &&
    currentDebouncedTrimmed === '' &&
    !appliedFilters.statusOptions.some((opt) => opt.checked) &&
    !appliedFilters.availabilityOptions.some((opt) => opt.checked);

  return {
    technicians,
    loading,
    accessError,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing: isSearchClearing || justClearedSearch,
    setSearch: handleSearchChange,
    statusOptions,
    setStatusOptions,
    availabilityOptions,
    setAvailabilityOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetchTechnicians: fetchTechnicians,
  };
};
